import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import axios from "axios";
import VueCryptojs from "vue-cryptojs";

// Global Components
import MandatoryIcon from "./components/MandatoryIcon.vue";
import AlertBox from "./components/AlertBox.vue";
import CardLoader from "./components/CardLoader.vue";
import CloseBtn from "@/components/CloseBtn.vue";
import GoBack from "@/components/GoBack.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import MasterPopup from "@/components/MasterPopup.vue";
import MessageBox from "@/components/MessageBox.vue";
import Vue3SimpleHtml2pdf from "vue3-simple-html2pdf";
import PrintPreview from "@/components/PrintPreview.vue";
import EditBtn from "@/components/FormButtons/EditBtn.vue";
import DelBtn from "@/components/FormButtons/DelBtn.vue";
import AddBtn from "@/components/FormButtons/AddBtn.vue";
import ExportBtn from "@/components/FormButtons/ExportBtn.vue";
import SaveBtn from "@/components/FormButtons/SaveBtn.vue";
import CancelBtn from "@/components/FormButtons/CancelBtn.vue";
import RefreshBtn from "@/components/FormButtons/RefreshBtn.vue";
import FilterBtn from "@/components/FormButtons/FilterBtn.vue";
import ClearfilterBtn from "@/components/FormButtons/ClearfilterBtn.vue";
import PopupLoader from "@/components/PopupLoader.vue";
import ExportfaBtn from "@/components/FormButtons/ExportfaBtn.vue";
import PrintBtn from "@/components/FormButtons/PrintBtn.vue";
import CancelFormbtn from "@/components/FormButtons/CancelFormbtn.vue";

// import VueQRCodeComponent from "vue-qrcode-component";
// Production;
// axios.defaults.baseURL = "https://chd.kvrtech.in/php/";
axios.defaults.baseURL = "https://chd.kvrtechno.in/php/";

// Testing
// axios.defaults.baseURL = "http://192.168.18.51:8880/chandigarh/kanda/src/php/";

const app = createApp(App);
app.use(store);
app.use(VueCryptojs);
app.use(router);
app.use(Vue3SimpleHtml2pdf);

// app.component("qr-code", VueQRCodeComponent);
app.component("MandatoryIcon", MandatoryIcon);
app.component("AlertBox", AlertBox);
app.component("CardLoader", CardLoader);
app.component("CloseBtn", CloseBtn);
app.component("GoBack", GoBack);
app.component("ConfirmBox", ConfirmBox);
app.component("MessageBox", MessageBox);
app.component("PrintPreview", PrintPreview);
app.component("MasterPopup", MasterPopup);
app.component("EditBtn", EditBtn);
app.component("DelBtn", DelBtn);
app.component("AddBtn", AddBtn);
app.component("ExportBtn", ExportBtn);
app.component("SaveBtn", SaveBtn);
app.component("CancelBtn", CancelBtn);
app.component("RefreshBtn", RefreshBtn);
app.component("FilterBtn", FilterBtn);
app.component("ClearfilterBtn", ClearfilterBtn);
app.component("PopupLoader", PopupLoader);
app.component("ExportfaBtn", ExportfaBtn);
app.component("PrintBtn", PrintBtn);
app.component("CancelFormbtn", CancelFormbtn);

// On Press Enter to shift to next element
app.directive("enter-next", {
  mounted(el) {
    const handler = (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();

      // Collect all focusable elements
      const focusable = Array.from(
        document.querySelectorAll(
          [
            'input:not([disabled]):not([type="hidden"])',
            "select:not([disabled])",
            "textarea:not([disabled])",
            "button:not([disabled])",
            '[tabindex]:not([tabindex="-1"])',
          ].join(", "),
        ),
      ).filter((node) => isVisible(node));

      // Sort by tabindex (default 0 if not set), then DOM order
      focusable.sort((a, b) => {
        const ta = a.tabIndex || 0;
        const tb = b.tabIndex || 0;
        if (ta === tb) {
          return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING
            ? 1
            : -1;
        }
        return ta - tb;
      });

      const index = focusable.indexOf(el);
      if (index > -1 && index + 1 < focusable.length) {
        focusable[index + 1].focus();
      }
    };

    el.__enterNextHandler__ = handler;
    el.addEventListener("keydown", handler);
  },
  unmounted(el) {
    el.removeEventListener("keydown", el.__enterNextHandler__);
    delete el.__enterNextHandler__;
  },
});

function isVisible(node) {
  const style = window.getComputedStyle(node);
  if (style.visibility === "hidden" || style.display === "none") return false;
  const rect = node.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

// For Select on Focus
app.directive("select-on-focus", {
  mounted(el) {
    el.addEventListener("focus", () => {
      // Ensure the element supports selection
      if (typeof el.setSelectionRange === "function") {
        // For text-like inputs
        el.setSelectionRange(0, el.value.length);
      } else {
        // Fallback: select the whole value (works for number inputs in most browsers)
        el.select?.();
      }
    });
  },
});

// For Numeric Only
app.directive("num-only", {
  mounted(el, binding) {
    let precision = 0;
    let allowNegative = false;
    if (binding.arg == undefined || (null && binding.value == "object")) {
      allowNegative = Boolean(
        binding.value.signed || binding.value.allowNegative,
      );
      precision = Number(binding.value.precision) || 0;
    } else {
      allowNegative = binding.arg === "signed";
    }

    // Auto-select on focus
    el.addEventListener("focus", () => {
      if (typeof el.setSelectionRange === "function") {
        el.setSelectionRange(0, el.value.length);
      } else {
        el.select?.();
      }
    });

    // Validation on input
    el.addEventListener("input", () => {
      let value = el.value;
      const regex = allowNegative ? /[^0-9.-]/g : /[^0-9.]/g;
      value = value.replace(regex, "");

      if (allowNegative) {
        value = value.replace(/(?!^)-/g, "");
      }

      const parts = value.split(".");
      if (parts.length > 2) {
        value = parts[0] + "." + parts[1];
      }

      if (precision > 0 && parts[1]) {
        parts[1] = parts[1].slice(0, precision);
        value = parts.join(".");
      } else if (precision === 0) {
        value = value.replace(/\..*/, "");
      }

      // Only update if different
      if (el.value !== value) {
        el.value = value;
        // Trigger v-model update once
        el.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    // Format on blur
    el.addEventListener("blur", () => {
      let value = el.value;
      if (value) {
        if (precision > 0) {
          value = parseFloat(value).toFixed(precision);
        } else {
          value = parseInt(value, 10).toString();
        }
        if (el.value !== value) {
          el.value = value;
          el.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    });
  },
});

app.mount("#app");
//createApp(App).use(store).use(router).mount('#app')
